/**
 * @changelog
 * | 版本   | 变更说明                        | 关联                    |
 * | v1.0.0 | 版本管理工具：清单加载+当前版本+切换 | REQ: 版本管理与快速切换  |
 * | v2.0.0 | buildNum 替代 tag 作为主标识；tag 降级为展示字段 | REQ: 版本管理与快速切换  |
 * | v3.0.0 | 新增默认版本机制：fetchDefaultVersion + getExplicitVersion | REQ: 场景默认版本 |
 * /@changelog
 *
 * @author v_djlzhou
 */

declare global {
  interface Window {
    __defaultVersion?: string;
  }
}

export interface VersionRecord {
  buildNum: string; // CI 构建号（主标识，用于归档/匹配/切换）
  tag: string; // git tag 派生（仅展示用，不参与功能逻辑）
  sdkVersion: string;
  publishedAt: string;
  commitHash: string;
  commitMessage: string;
  pipelineId: string;
  publisher: string;
  releasePath: string;
  resources: Record<string, string>; // 逻辑名 → 资源文件名（相对路径，boot script 拼 ./releases/{buildNum}/assets/{file}）
}

export interface VersionManifest {
  latest: string;
  updatedAt: string;
  versions: VersionRecord[];
}

const VERSION_STORAGE_KEY = 'viewer_version';

/**
 * 默认版本配置文件 URL（固定 COS 地址）
 *
 * 所有环境共用同一份配置，由运营在 COS 上维护。
 * 返回数组 [{env:'dev',defaultVersion:510}, {env:'prod',defaultVersion:519}]，
 * 按 hostname/pathname 判断环境：localhost 或 /dev/ → dev，否则 prod。
 */
const SCENE_DEFAULT_VERSION_URL = 'https://mogs-1300354879.cos.ap-shanghai.myqcloud.com/mogs/nobody/gs-viewer/config/scene_default_version.json';

interface DefaultVersionEntry {
  env: string;
  defaultVersion: number;
}

/**
 * 请求 COS 上的默认版本配置数组，按当前环境（dev/prod）取对应 defaultVersion。
 * 优先读 boot script 已写入的 window.__defaultVersion，避免重复请求。
 */
export async function fetchDefaultVersion(): Promise<string | null> {
  // boot script 已同步请求过，直接复用
  if (window.__defaultVersion) {
    return window.__defaultVersion;
  }

  try {
    const res = await fetch(SCENE_DEFAULT_VERSION_URL, { cache: 'no-cache' });
    if (!res.ok) return null;
    const data = await res.json();
    if (Array.isArray(data)) {
      const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.pathname.indexOf('/dev/') >= 0;
      const env = isDev ? 'dev' : 'prod';
      const item = (data as DefaultVersionEntry[]).find((d) => d.env === env);
      if (item && item.defaultVersion) {
        return String(item.defaultVersion);
      }
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * 获取当前页面已加载的版本
 * 策略：检测已加载 script 的 src 路径 → localStorage → 默认 'latest'
 */
export function getCurrentVersion(): string {
  // 方法1：从已加载脚本 URL 中提取 releases/{buildNum}/ 路径
  const scripts = document.querySelectorAll<HTMLScriptElement>('script[src]');
  for (let i = 0; i < scripts.length; i++) {
    const match = scripts[i].src.match(/\/releases\/(\d+)\//);
    if (match) return match[1];
  }

  // 方法2：回退到 localStorage
  try {
    const stored = localStorage.getItem(VERSION_STORAGE_KEY);
    if (stored) return stored;
  } catch {
    /* 静默降级 */
  }

  return 'latest';
}

/**
 * 从 COS 加载版本清单
 * @returns 版本清单，加载失败时返回 null
 */
export async function fetchVersions(): Promise<VersionManifest | null> {
  try {
    // 加时间戳查询参数强制绕过浏览器/CDN 缓存
    const res = await fetch(`./versions.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const data: VersionManifest = await res.json();
    return data;
  } catch {
    return null;
  }
}

/**
 * 获取用户显式指定的版本（URL 参数或 localStorage）
 *
 * 优先级：hash 查询参数 > search 查询参数（旧 URL 兼容）> localStorage
 *
 * 用于 ModelList.vue 下拉框初始选中：
 * - 有显式版本 → 选中对应版本号
 * - 无显式版本 → 选中"自动"（跟随默认版本）
 *
 * 注意：此逻辑与 vite.config.js boot script 中的版本判定重复，
 * 因为 boot script 是纯 JS 字符串无法 import TS。两处需同步维护。
 *
 * @returns 显式版本 buildNum 字符串，无显式指定时返回 null
 */
export function getExplicitVersion(): string | null {
  // 检查 hash 查询参数
  const hashQuery = (window.location.hash || '').split('?')[1] || '';
  const hashVersion = new URLSearchParams(hashQuery).get('version');
  if (hashVersion) return hashVersion;

  // 检查 search 查询参数（旧 URL 兼容）
  const searchVersion = new URLSearchParams(window.location.search).get('version');
  if (searchVersion) return searchVersion;

  // 检查 localStorage
  try {
    const stored = localStorage.getItem(VERSION_STORAGE_KEY);
    if (stored) return stored;
  } catch {
    /* 静默 */
  }

  return null;
}

/**
 * 切换到指定版本
 * @param version 目标版本 buildNum，传 null/空字符串表示切回自动（跟随默认版本）
 */
export function switchVersion(version: string | null): void {
  if (version) {
    try {
      localStorage.setItem(VERSION_STORAGE_KEY, version);
    } catch {
      /* 静默降级 */
    }
  } else {
    try {
      localStorage.removeItem(VERSION_STORAGE_KEY);
    } catch {
      /* 静默降级 */
    }
  }

  // 将 version 放入 hash 查询字符串，避免 location.search 与 hash 路由自身的 ? 冲突
  const hash = window.location.hash || '#/';
  const qIdx = hash.indexOf('?');
  const hashPath = qIdx >= 0 ? hash.slice(0, qIdx) : hash;
  const hashQuery = qIdx >= 0 ? hash.slice(qIdx + 1) : '';
  const params = new URLSearchParams(hashQuery);
  if (version) {
    params.set('version', version);
  } else {
    params.delete('version');
  }
  const qs = params.toString();
  const newHash = hashPath + (qs ? '?' + qs : '');

  // 保留 origin + pathname，丢弃旧 search，更新 hash
  // 仅改 hash 不会触发整页刷新，需 replaceState 更新 URL 后显式 reload
  const newUrl = window.location.origin + window.location.pathname + newHash;
  window.history.replaceState(null, '', newUrl);
  window.location.reload();
}
